import { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import banners, { BannerTypes } from '@gin-betting/banners';
import core, { App, GQL } from '@gin-betting/core';
import { createStructuredSelector } from 'reselect';

import { CLASS_NAME_UPDATE_COLORS } from 'app-constants';
import {
    useCssClassToggleOnDocument,
    useMount,
    usePrevious,
    useWithLocation,
} from 'hooks';
import {
    BettingReadyIndicatorContext,
    initBettingReadyIndicator,
} from 'modules/betting-ready-indicator';
import { AsyncMatch } from 'modules/match/async';
import promo from 'modules/promo';
import features from 'services/feature-manager';
import type { State } from 'types';
import { checkIfNewsPage } from 'utils';
import { checkIsBannerCarouselPage, layoutChanger } from './utils';

const {
    entities: {
        actions: { getSports, getMarketTemplates },
    },
    viewport: {
        selectors: { makeViewportSelector, selectIsCompact },
    },
    utils: { getBettingOptions, checkOnRoute, getSportTypeFromLocation },
    constants: { isMobile },
} = core;

const {
    selectors: { selectAreSportsAvailable },
} = promo;

const {
    actions: { getAvailableBannerZones },
} = banners;

interface Output {
    areSportsAvailable: ReturnType<typeof selectAreSportsAvailable>;
    isAuthorized: boolean;
    isNewsPage: boolean;
    isBannersCarouselVisible: boolean;
    isOutrightsPage: boolean;
    sportType: App.SportType;
    shouldRenderTopMatches: boolean;
}
const cachedSportTypes = <string[]>[];

const useApp = (): Output => {
    const dispatch = useDispatch();
    const readyIndicator = useContext(BettingReadyIndicatorContext);
    const {
        lessThanOrEqual,
        areSportsAvailable,
        isCompact,
    } = useSelector((state: State) => selectProps(state, {}));
    const { isAuthorized } = getBettingOptions();
    const isNewsPage = checkIfNewsPage();
    const {
        sportType,
        isBannersCarouselVisible,
        isOutrightsPage,
        location,
        isMainPage,
    } = useWithLocation(currLocation => ({
        sportType: getSportTypeFromLocation(),
        isBannersCarouselVisible:
            lessThanOrEqual.small && checkIsBannerCarouselPage(),
        isMainPage:
            getBettingOptions().url.pagePath === '' &&
            checkOnRoute(App.Routes.UPCOMING_LIST, { exact: true }),
        isOutrightsPage:
            checkOnRoute(App.Routes.OUTRIGHTS_LIST) &&
            currLocation.search === '',
        location: currLocation,
    }));

    const isUpdateColors = features.isActive(features.list.AppUpdateColors);

    useCssClassToggleOnDocument(CLASS_NAME_UPDATE_COLORS, isUpdateColors);

    useMount(() => {
        layoutChanger();
    });

    const previousLocation = usePrevious(location);

    useEffect(() => {
        window.__app.previousLocation = previousLocation;
    }, [previousLocation]);

    useMount(() => {
        const { sportIds } = getBettingOptions();

        dispatch(getSports({ sportIds }));

        initBettingReadyIndicator(readyIndicator.initIndicator);

        setTimeout(() => {
            AsyncMatch.preload();
        }, 5000);

        dispatch(
            getMarketTemplates({
                sportIds,
                marketType: GQL.MarketType.MATCH,
            })
        );
    });

    useEffect(() => {
        if (!cachedSportTypes.includes(sportType)) {
            cachedSportTypes.push(sportType);
            dispatch(
                getAvailableBannerZones({
                    pageType: sportType,
                    platform: isMobile
                        ? BannerTypes.BannerZonePlatform.MOBILE
                        : BannerTypes.BannerZonePlatform.DESKTOP,
                })
            );
        }
    }, [sportType, dispatch]);

    const shouldRenderTopMatches = isMainPage && !isCompact;

    return {
        areSportsAvailable,
        isAuthorized,
        isNewsPage,
        isBannersCarouselVisible,
        isOutrightsPage,
        sportType,
        shouldRenderTopMatches,
    };
};

const selectProps = createStructuredSelector({
    lessThanOrEqual: makeViewportSelector('lessThanOrEqual'),
    areSportsAvailable: selectAreSportsAvailable,
    isCompact: selectIsCompact,
});

export default useApp;
